import { prepareReportPhotos } from "../shared/prepareReportPhotos";

import {
  makeDeleteApiCall,
  makeGetApiCall,
  makeGetBlobApiCall,
  makePatchApiCall,
  makePostApiCall,
} from "../shared/makeApiCall";
import {
  ShippingReportImageType,
  ShippingReportUpdate,
} from "../types/shippingReports";

async function postPhotos(endpoint: string, files: File[], createForm: (photos: File[]) => FormData) {
  let photos: File[];
  try {
    photos = await prepareReportPhotos(files);
  } catch (error) {
    return { success: false, data: { message: error instanceof Error ? error.message : "Could not prepare the picture. Please try again." } };
  }
  try {
    const result = await makePostApiCall(endpoint, createForm(photos));
    if ("errorStatus" in result && result.errorStatus === 413) {
      return { ...result, data: { message: "These pictures exceed the server upload limit. Try fewer or smaller pictures." } };
    }
    return result;
  } catch {
    return { success: false, data: { message: "Could not upload the picture. Check your connection and try again." } };
  }
}

export const shippingReportsApi: Record<
  string,
  (...args: any[]) => Promise<any>
> = {
  createForShipping: (shippingId: number | string) =>
    makePostApiCall(`shippings/${shippingId}/report`),

  createForSunOrder: (sunOrderId: number | string) =>
    makePostApiCall(`sun-orders/${sunOrderId}/report`),

  availableShipments: (reportId: string, search = "") =>
    makeGetApiCall(`shipping-reports/${reportId}/available-shipments?search=${encodeURIComponent(search)}`),
  attachShipments: (reportId: string, shippingIds: number[]) =>
    makePostApiCall(`shipping-reports/${reportId}/shipments`, { shipping_ids: shippingIds }),
  detachShipment: (reportId: string, shippingId: number) =>
    makeDeleteApiCall(`shipping-reports/${reportId}/shipments/${shippingId}`),

  get: (reportId: number | string) =>
    makeGetApiCall(`shipping-reports/${reportId}`),

  update: (reportId: number | string, values: ShippingReportUpdate) =>
    makePatchApiCall(`shipping-reports/${reportId}`, values),

  complete: (reportId: number | string) =>
    makePostApiCall(`shipping-reports/${reportId}/complete`),

  reopen: (reportId: number | string) =>
    makePostApiCall(`shipping-reports/${reportId}/reopen`),

  uploadImages: (
    reportId: number | string,
    type: ShippingReportImageType,
    files: File[],
  ) => {
    return postPhotos(`shipping-reports/${reportId}/images`, files, photos => {
      const formData = new FormData();
      formData.append("type", type);
      photos.forEach(file => formData.append("images[]", file));
      return formData;
    });
  },

  updateImage: (
    imageId: number,
    values: { caption?: string | null; type?: ShippingReportImageType },
  ) => makePatchApiCall(`shipping-report-images/${imageId}`, values),

  deleteImage: (imageId: number) =>
    makeDeleteApiCall(`shipping-report-images/${imageId}`),

  reorderImages: (reportId: number | string, imageIds: number[]) =>
    makePostApiCall(`shipping-reports/${reportId}/images/reorder`, {
      image_ids: imageIds,
    }),

  downloadPdf: (reportId: number | string) =>
    makeGetBlobApiCall(`shipping-reports/${reportId}/pdf`),

  createArrivalTemperature: (
    reportId: number | string,
    values: {
      temperatureCelsius: string;
      image: File;
      note?: string;
    },
  ) => {
    return postPhotos(`shipping-reports/${reportId}/arrival-temperatures`, [values.image], photos => {
      const formData = new FormData();
      formData.append("temperature_celsius", values.temperatureCelsius);
      formData.append("image", photos[0]);
      if (values.note) formData.append("note", values.note);
      return formData;
    });
  },

  deleteArrivalTemperature: (readingId: number) =>
    makeDeleteApiCall(`shipping-report-arrival-temperatures/${readingId}`),
  createDepartureTemperature: (
    reportId: number | string,
    values: {
      temperatureCelsius: string;
      image: File;
      note?: string;
    },
  ) => {
    return postPhotos(`shipping-reports/${reportId}/departure-temperatures`, [values.image], photos => {
      const formData = new FormData();
      formData.append("temperature_celsius", values.temperatureCelsius);
      formData.append("image", photos[0]);
      if (values.note) formData.append("note", values.note);
      return formData;
    });
  },

  deleteDepartureTemperature: (readingId: number) =>
    makeDeleteApiCall(`shipping-report-departure-temperatures/${readingId}`),
};
